/**
 * useOfficeViewMode — the View/Edit toggle + Edit→View save gate for the live
 * office editors (#620, Task B2/B3).
 *
 * View mode renders officecli's HTML against the CURRENT HEAD (OfficeCliRender,
 * no versionId). Because the head is the last SAVED blob, switching Edit→View
 * while dirty must (a) force a save and (b) wait for the write to PERSIST before
 * rendering — `onSaveRef` only posts Action_Save (fire-and-forget), so we poll
 * getFile metadata until `contentHash` changes, NOT a realtime event (the
 * Collabora PutFile path emits none). readOnly permission forces View.
 */
import { useState, useCallback, useEffect, useRef, type ReactNode } from 'react'
import { Eye, Pencil } from 'lucide-react'
import { fetchRaw } from '@papan/api-client'
import { ModeToggle } from './ModeToggle.js'

const OOXML = new Set(['xlsx', 'docx', 'pptx'])

async function headContentHash(fileId: string): Promise<string | null> {
  try {
    const r = await fetchRaw(`/api/files/${fileId}`)
    if (!r.ok) return null
    const j = await r.json() as { contentHash?: string | null }
    return j.contentHash ?? null
  } catch { return null }
}

// The dirty Edit→View poll waits this long for the forced save to persist
// before mounting the render anyway. Generous so a slow Collabora PutFile is
// caught (View mounts only after the head hash moves, then fetches the fresh
// head) rather than timing out and mounting on stale bytes; a genuine hang
// still never wedges the toggle. Overridable for tests.
const SAVE_PERSIST_DEADLINE_MS = 15_000

export function useOfficeViewMode(opts: {
  readOnly?: boolean
  fileId: string
  fileName?: string
  isDirty: boolean
  onSaveRef?: React.MutableRefObject<(() => void) | null>
  /** A pinned historical version → the officecli View is disabled (it renders the
   *  HEAD, no versionId; the read-only versioned editor must stay authoritative). */
  versionId?: string
  /** Test seam: shorten the Edit→View save-persist poll deadline. */
  persistDeadlineMs?: number
  /** No officecli View at all — the OnlyOffice editor is the only face (read-only or
   *  edit). The share view: its principal cannot reach the officecli render route. */
  disabled?: boolean
}): { viewMode: 'view' | 'edit'; viewReady: boolean; showToggle: boolean; toggleNode: ReactNode } {
  const { readOnly, fileId, fileName, isDirty, onSaveRef, versionId, persistDeadlineMs, disabled } = opts
  const ext = fileName?.split('.').pop()?.toLowerCase() || ''
  // The toggle appears for any OOXML file officecli can serve (#620 Phase C: the
  // officecli View is the default render path, no flag). It is suppressed for a pinned
  // VERSION: OfficeCliRender in the View branch renders the HEAD (no versionId), so
  // pinning View on a version mount would show the wrong (current) content and the
  // failure-fallback would resolve the latest version, not the pinned one. A versionId
  // mount stays on the read-only versioned editor.
  const showToggle = OOXML.has(ext) && !versionId && !disabled
  // Pin a read-only file to View ONLY when officecli can serve it. With the flag
  // off / non-OOXML we leave it in 'edit' so the wrapper renders a SINGLE
  // read-only editor — pinning View there would strand a wasted WOPI session
  // (and, via the fallback, a second one). See finding #1.
  const pinView = !!readOnly && showToggle

  // Documents OPEN in View by default whenever officecli can serve them: the
  // lightweight officecli render loads first, and the heavier OnlyOffice editor
  // mounts only when the user enters Edit. (Flag off / non-OOXML → 'edit', the
  // single read-only-or-edit OnlyOffice editor, unchanged.)
  const [viewMode, setViewMode] = useState<'view' | 'edit'>(showToggle ? 'view' : 'edit')
  // A fresh open is clean, so View is immediately ready — the Edit→View save-gate
  // below only applies to a toggle made with unsaved edits.
  const [viewReady, setViewReady] = useState<boolean>(showToggle)
  const dirtyRef = useRef(isDirty); dirtyRef.current = isDirty
  // Generation guard: invalidates an in-flight enterView poll when the user
  // toggles again (View→Edit→View), so a stale loop can't flip viewReady.
  const genRef = useRef(0)

  // Re-pin a readOnly file to View if the permission resolves late (async).
  useEffect(() => { if (pinView) { setViewMode('view'); setViewReady(true) } }, [pinView])

  // Invalidate any in-flight enterView poll on unmount (bump the generation the
  // loop checks) so it stops fetching and never calls setViewReady on an unmounted
  // component. StrictMode's mount/unmount/mount double-invoke is covered too.
  useEffect(() => () => { genRef.current++ }, [])

  const enterView = useCallback(async () => {
    const gen = ++genRef.current
    setViewMode('view')
    if (!dirtyRef.current) { if (gen === genRef.current) setViewReady(true); return }
    // Dirty: force a save, then wait for the persisted head to actually change
    // (poll getFile.contentHash) before rendering, or officecli renders stale.
    setViewReady(false)
    // Baseline the pre-save head hash. If that read FAILS (null), we can't compare —
    // treating "any hash differs from null" as persisted breaks the gate on the first
    // ~400ms poll and renders stale bytes. Instead anchor the baseline to the first
    // successful poll read and wait for a change FROM it (or the deadline): the render
    // is always the post-save head, at worst a little later, never pre-save.
    let baseline = await headContentHash(fileId)
    onSaveRef?.current?.()
    const deadline = Date.now() + (persistDeadlineMs ?? SAVE_PERSIST_DEADLINE_MS)
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 400))
      if (gen !== genRef.current) return // superseded by a newer toggle
      const now = await headContentHash(fileId)
      if (!now) continue
      if (baseline === null) { baseline = now; continue } // no pre-save baseline → anchor, wait for a change
      if (now !== baseline) break
    }
    // Mount the render once the head has persisted (the poll saw the hash move)
    // — OfficeCliRender then fetches the fresh head on mount. Render even if the
    // poll timed out so the toggle never hangs; a save that lands after the
    // deadline shows on the next Edit→View toggle (which re-polls).
    if (gen === genRef.current) setViewReady(true)
  }, [fileId, onSaveRef, persistDeadlineMs])

  const onChange = useCallback((m: 'view' | 'edit') => {
    if (readOnly) return
    if (m === 'view') void enterView()
    else { genRef.current++; setViewMode('edit'); setViewReady(false) }
  }, [readOnly, enterView])

  const toggleNode: ReactNode = (
    <ModeToggle
      options={[
        { value: 'view', label: 'View', icon: <Eye size={14} /> },
        { value: 'edit', label: 'Edit', icon: <Pencil size={14} />, title: readOnly ? 'Read-only file' : undefined },
      ]}
      value={viewMode}
      onChange={onChange}
    />
  )

  return { viewMode, viewReady, showToggle, toggleNode }
}
